import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { ADD_JOB } from "../../utils/graphql/jobs/mutations.js";
import { QUERY_EVENTS } from "../../utils/graphql/gcal/queries.js";
import { GET_USERS } from "../../utils/graphql/users/queries.js";
import { QUERY_SYSTEM_SETTING } from "../../utils/graphql/systemSettings/queries";
import { URLS } from "../../config/urls";

import CalendarListView from "../CalendarListView";

const JobForm = ({ me, onRefetch, onSuccess }) => {

  console.log("Rendering JobForm", me);
  const isAdmin = me?.admin === true;

  const calendarId = isAdmin ? "meetings@oplm.com" : "primary";

  const { data: googleIntegrationSettingData } = useQuery(QUERY_SYSTEM_SETTING, {
    variables: { key: "googleWorkspaceIntegrationEnabled" },
  });
  const googleWorkspaceIntegrationEnabled =
    String(googleIntegrationSettingData?.systemSetting?.value ?? "true").toLowerCase() !== "false";

  const { data, loading: meetingsLoading, error: eventsError } = useQuery(QUERY_EVENTS, {
    variables: { calendarId: calendarId },
    skip: !googleWorkspaceIntegrationEnabled,
  });

  const { data: usersData } = useQuery(GET_USERS);

  const users = usersData?.users ?? [];
  const userOptions = users?.map(u => ({
    value: u._id.toString(),
    label: u.username
  }));

  console.log(userOptions);
  const meetings = data || [];
  const events = data?.googleEvents || [];

  console.log("Events from JobForm: ", events);
  const [jobText, setText] = useState({
    active: true,
    description: "",
    meetings: [], // note plural
  });
  const [characterCount, setCharacterCount] = useState(0);

  const [addJob, { error }] = useMutation(ADD_JOB, {
    fetchPolicy: "no-cache",
  });
  
  const [formError, setFormError] = useState("");
  const [selectedEvents, setSelectedEvents] = useState(new Set());

  const [formData, setFormData] = useState({
    createdBy: "",
  });

  // Update state for textarea
  const handleChange = (event) => {
    const { name, value } = event.target;
    setText((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "description") {
      setCharacterCount(value.length);
    }
  };

  // Update state for textarea
  const handleSelectionChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleEventClick = (info) => {
    const id = info.event.id;
    const newSet = new Set(selectedEvents);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedEvents(newSet);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!googleWorkspaceIntegrationEnabled) {
      setFormError("Google integrations are currently disabled by an administrator.");
      return;
    }

    // Default to current user
    let creator = me?._id;

    // Admins choose a user from the dropdown
    if (isAdmin) {
      creator = formData.createdBy;

      if (!creator) {
        setFormError("You need to select a user before submitting the form.");
        return;
      }
    }

    const meetingsMap = new Map(events.map((e) => [e.id, e]));

    try {
      for (const eventId of selectedEvents) {
        const meeting = meetingsMap.get(eventId);
        const { data } = await addJob({
          variables: {
            description: jobText.description,
            createdBy: creator,
            meeting: meeting.id,
            calendarId: calendarId,
          },
        });

        if (data.addJob.conflict) {
          setFormError("This meeting conflicts with an existing one.");
          return; // stop here but don't reset form
        }
      }

      // Only reset after successful submission
      setText({ active: true, description: "", meetings: [] });
      setCharacterCount(0);
      setFormData({ createdBy: "" }); // clear dropdown only on success

      onSuccess?.();
      // await onRefetch();
      setTimeout(() => onRefetch(), 0);
    } catch (e) {
      console.error(e);
    }
  };

  if (!googleWorkspaceIntegrationEnabled) {
    return (
      <div className="alert alert-warning" role="alert">
        Google Calendar integration is currently disabled by an administrator.
      </div>
    );
  }

  if (meetingsLoading) return <p>Loading meetings...</p>;

  if (eventsError) {
    return (
      <div className="alert alert-danger" role="alert">
        Unable to load your calendar. Your Google Calendar connection may have expired.
        Please <a href={URLS.googleAuth}>re-authenticate with Google</a> and try again.
      </div>
    );
  }

  return (
    <>
      <form
        className="flex-row justify-center justify-space-between-md align-stretch"
        onSubmit={handleFormSubmit}
      >
        <div className="meeting-calendar d-flex justify-content-center align-items-center col-12 col-md-12">
          <CalendarListView
            meetings={meetings}
            selectedEvents={selectedEvents}
            onEventClick={handleEventClick}
          />
        </div>
        {(isAdmin) && (
          <>
            <label className="text-dark pt-4">For:</label>
            <select
              name="createdBy"
              id="createdBy"
              className="form-input col-12 col-md-12"
              value={formData.createdBy}
              onChange={handleSelectionChange}
            >
              <option value="">-- Select user --</option>
              {userOptions.map((user) => (
                <option key={user.value} value={user.value}>
                  {user.label}
                </option>
              ))}
            </select>
            <br />
          </>
        )}

        <label className="text-dark pt-4">Notes:</label>
        <textarea
          placeholder="Any additional information you'd like to include."
          name="description"
          id="description"
          className="form-input col-12 col-md-12"
          onChange={handleChange}
          value={jobText.description}
        ></textarea>
        Character Count: {characterCount}/280<br />
        <p
          className={`m-0 ${formError ? "text-error" : ""}`}
        >
          {formError && <span className="ml-2">{formError}</span>}
          {error && <span className="ml-2">Something went wrong...</span>}
        </p>
        <div className="w-75 mr-auto ml-auto text-center">
          <br />
          <button
            className="btn no-border-btn btn-primary mt-0 col-12 w-100 text-center"
            type="submit"
            disabled={meetingsLoading || selectedEvents.size === 0}
          >
            {meetingsLoading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </>
  );
};

export default JobForm;